import {useState, useEffect} from "react";
import styled from "styled-components";
import theme from "../../../UI/theme";
import { ModalText } from "../ModalStyles";
import Dropdown from "../../UiComponents/Dropdown";
import ToggleButton from "../../UiComponents/ToggleButton";
import SimpleSlider from "../../UiComponents/SimpleSlider";
import { updatePlaylist } from "../../../scripts/playlist";
import { TextMuted } from "../../UiComponents/text/StyledText";


const PlaylistItem = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-gap: 10px;
    padding: 10px 10px 10px 10px;
    box-sizing: border-box;
    border-radius: 2px;
    align-items: center;
    height: fit-content;
    row-gap: 10px;

    
    //border-bottom: 2px solid ${theme.colors.darkSpace80};
    background-color: ${theme.colors.darkSpace80};

    .slider-row {
        box-sizing: border-box;
        grid-column: 1 / -1;
        width: 100%;
        margin-top: 6px;
    }
`

const Container = styled.div`
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 10px;
    width: 100%;
    overflow: auto;
`

const PlaylistTitle = styled.label`
    font-size: ${theme.fontSize.medium};
`;

const TitleContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 5px;
`

/**
 * 
 TODO: if musicplayer.hasStoredPlaylists is false, disable the settings for virtual dj. add some flair to please fredrik
 */

const orderPlaylistsByItems = {
    MOOD_MATCH: {
        value: 'MOOD_MATCH',
        label: 'Mood Match',
    },
    PLAYLIST_NAME: {
        value: 'PLAYLIST_NAME',
        label: 'Playlist Name',
    },
    PLAYLIST_MOOD: {
        value: 'PLAYLIST_MOOD',
        label: 'Playlist Mood',
    },
    PLAYING: {
        value: 'PLAYING',
        label: 'Currently Playing',
    }
}

const DEFAULT_MOOD_SCORE = 5;

const PlaylistSettings = ({
    musicPlayer,
    defaultPlaylistOrder = 'MOOD_MATCH',
}) => {

    const [orderPlaylistsBy, setOrderPlaylistsBy] = useState(orderPlaylistsByItems[defaultPlaylistOrder] || orderPlaylistsByItems.MOOD_MATCH);
    const [useInVirtualDjValues, setUseInVirtualDjValues] = useState({});
    const [sliderValues, setSliderValues] = useState({});

    useEffect(() => {
        if (musicPlayer?.music?.availablePlaylists?.length) {
            const initialSliderValues = {};
            const initialUseInVirtualDjValues = {};
            musicPlayer.music.availablePlaylists.forEach(playlist => {
                const remotePlaylistId = getRemotePlaylistKey(playlist);
                initialSliderValues[remotePlaylistId] = playlist?.moodScore || DEFAULT_MOOD_SCORE;
                initialUseInVirtualDjValues[remotePlaylistId] = playlist?.useInVirtualDj || false;
            });
            setSliderValues(initialSliderValues);
            setUseInVirtualDjValues(initialUseInVirtualDjValues);
        }
    }, [musicPlayer?.music?.availablePlaylists]);

    const getRemotePlaylistKey = (p) => p?.remotePlaylistId || p?.id;

    const orderPlaylists = (a, b)  => {
        switch (orderPlaylistsBy.value) {
            case orderPlaylistsByItems.MOOD_MATCH.value:
                return b.moodMatch - a.moodMatch;
            case orderPlaylistsByItems.PLAYLIST_NAME.value:
                return a.name.localeCompare(b.name);
            case orderPlaylistsByItems.PLAYLIST_MOOD.value:
                return (b.moodScore || DEFAULT_MOOD_SCORE) - (a.moodScore || DEFAULT_MOOD_SCORE);
            case orderPlaylistsByItems.PLAYING.value:
                return 0;
            default:
                return 0;
        }
    }


    return (
        <Container>
            <TextMuted>Order playlists:</TextMuted>
            <Dropdown
                small
                items={Object.values(orderPlaylistsByItems)}
                selectedItem={orderPlaylistsBy}
                onClick={(item) => setOrderPlaylistsBy(item)} 
            />

            <br></br>
            <TextMuted>Your Playlists</TextMuted>

            {[...(musicPlayer?.music?.availablePlaylists || [])]
                .sort((a, b) => {

                    const aUse = useInVirtualDjValues[a.remotePlaylistId] ?? a.useInVirtualDj ?? false;
                    const bUse = useInVirtualDjValues[b.remotePlaylistId] ?? b.useInVirtualDj ?? false;

                    // First, prioritize playlists used in Virtual DJ if Virtual DJ mode is ON and ordering by MOOD MATCH
                    if (musicPlayer?.virtualDjMode=== 'ON' && orderPlaylistsBy.value === orderPlaylistsByItems.MOOD_MATCH.value) {
                        if (aUse && !bUse) return -1;
                        if (!aUse && bUse) return 1;
                    }
                    
                    // Finally, apply the selected ordering
                    return orderPlaylists(a, b);
                })
                .map((playlist, index) => {

                const remotePlaylistId = getRemotePlaylistKey(playlist);
                const currentValue = sliderValues[remotePlaylistId] ?? 5;
                const useInVirtualDj = useInVirtualDjValues[remotePlaylistId] ?? false;

                return <PlaylistItem
                        key={remotePlaylistId}
                    >
                    <TitleContainer>
                        <PlaylistTitle>{playlist?.name}</PlaylistTitle>
                        <div style={{display: 'flex', gap: '10px', marginTop: '5px', alignItems: 'center'}}>
                            <TextMuted>Add to Autopilot</TextMuted>
                            <ToggleButton
                                checked={useInVirtualDj}
                                onToggle={(checked) => {
                                    setUseInVirtualDjValues(prev => ({
                                        ...prev,
                                        [remotePlaylistId]: checked,
                                    }));

                                    const updated = {
                                        playlistId: playlist.playlistId,
                                        useInVirtualDj: checked,
                                        moodScore: playlist.moodScore || DEFAULT_MOOD_SCORE, // if 0, undefined or null, set to default
                                    }

                                    updatePlaylist(updated)
                                        .catch(err => {
                                            console.error("Failed to update useInVirtualDj value:", err);
                                        });
                                }}
                            />
                        </div>
                    </TitleContainer>
                    
                    {useInVirtualDj && (
                        <div className="slider-row">
                            <SimpleSlider
                                title={"Set the Playlist Mood"}
                                max={10}
                                min={1}
                                step={0.1}
                                value={currentValue}
                                onChange={([newValue]) => {
                                    setSliderValues(prev => ({
                                        ...prev,
                                        [remotePlaylistId]: newValue,
                                    }));
                                }}
                                footerLables={['Chill', 'Balanced', 'Upbeat']}
                                
                                onChangeCommitted={(newValue) => {
                                    playlist.moodScore = newValue;
                                    const updated = {
                                        playlistId: playlist.playlistId,
                                        moodScore: newValue,
                                    }
                                    updatePlaylist(updated)
                                        .catch(err => {
                                            console.error("Failed to update playlist moodScore:", err);
                                        });
                                }}
                            />
                        </div>
                    )}
                </PlaylistItem>
            })}
        </Container>

    )

}

export default PlaylistSettings;
